import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, Receipt, FileText, DownloadSimple, Tag, UserCircle, Car, Camera, X, Buildings, Clock } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VEHICLE_TYPES = [
  { value: 'utilitaire', label: 'Utilitaire' },
  { value: 'tourisme', label: 'Tourisme' }
];
const CONDITIONS = [
  { value: 'neuf', label: 'Neuf' },
  { value: 'bon', label: 'Bon etat' },
  { value: 'usage', label: 'Usage' },
  { value: 'reparation', label: 'En reparation' },
  { value: 'hors_service', label: 'Hors service' }
];

const conditionBadge = (cond) => {
  const colors = { neuf: '#2E7D32', bon: '#1565C0', usage: '#EF6C00', reparation: '#C62828', hors_service: '#424242' };
  const label = CONDITIONS.find(c => c.value === cond)?.label || cond;
  return <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: `${colors[cond] || '#666'}15`, color: colors[cond] || '#666' }}>{label}</span>;
};

// ---- Sub-components ----
const InlineAssignSelect = ({ vehicle, employees, onSaved }) => {
  const [val, setVal] = useState(vehicle.assigned_user_id || 'none');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(vehicle.assigned_user_id || 'none'); }, [vehicle.assigned_user_id]);
  const change = async (v) => {
    setVal(v);
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/vehicles/${vehicle.id}`, { assigned_user_id: v === 'none' ? '' : v }, { withCredentials: true });
      toast.success('Affectation mise a jour');
      if (onSaved) onSaved();
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Select value={val} onValueChange={change} disabled={saving}>
      <SelectTrigger style={{ width: '100%' }} data-testid="vehicle-detail-assign-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Aucun (non affecte)</SelectItem>
        {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const TripsTab = ({ vehicleId }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/vehicles/${vehicleId}/trips`, { withCredentials: true });
        setTrips(res.data || []);
      } catch (e) { toast.error('Erreur chargement trajets'); } finally { setLoading(false); }
    })();
  }, [vehicleId]);

  if (loading) return <div className="card" style={{ padding: '24px', textAlign: 'center', color: '#5E5F66' }}>Chargement...</div>;
  return (
    <div className="card" data-testid="vehicle-trips-tab">
      {trips.length === 0 ? (
        <div className="empty-state"><Clock size={48} className="empty-state-icon" /><p className="empty-state-text">Aucun trajet enregistre pour ce vehicule</p></div>
      ) : (
        <table className="data-table">
          <thead><tr><th>Date</th><th>Salarie</th><th>Chantier</th><th>Heures</th></tr></thead>
          <tbody>
            {trips.map(t => (
              <tr key={t.id} data-testid={`trip-row-${t.id}`}>
                <td>{t.date}</td>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><UserCircle size={18} color="#1565C0" /> {t.user_name || '-'}</div></td>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Buildings size={16} color="#002FA7" /> {t.project_name || '-'}</div></td>
                <td style={{ fontWeight: 600 }}>{t.hours}h</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const INSPECTION_CATEGORIES = {
  habitacle: { label: 'Habitacle', items: [
    { key: 'siege_propre', label: 'Sieges propres' },
    { key: 'tableau_bord_ok', label: 'Tableau de bord en etat' },
    { key: 'tapis_ok', label: 'Tapis presents et propres' },
    { key: 'odeur_ok', label: 'Aucune mauvaise odeur' },
    { key: 'climatisation_ok', label: 'Climatisation fonctionnelle' },
  ]},
  moteur: { label: 'Moteur', items: [
    { key: 'huile_ok', label: 'Niveau huile correct' },
    { key: 'liquide_refroid_ok', label: 'Liquide de refroidissement OK' },
    { key: 'liquide_freins_ok', label: 'Liquide de freins OK' },
    { key: 'lave_glace_ok', label: 'Lave-glace plein' },
    { key: 'fuites_aucune', label: 'Aucune fuite visible' },
  ]},
  carrosserie: { label: 'Carrosserie', items: [
    { key: 'aucun_choc', label: 'Aucun choc' },
    { key: 'aucune_rayure', label: 'Aucune rayure significative' },
    { key: 'pare_chocs_ok', label: 'Pare-chocs intacts' },
    { key: 'phares_feux_ok', label: 'Phares & feux fonctionnels' },
    { key: 'pneus_ok', label: 'Pneus en bon etat' },
  ]},
};

const InspectionsTab = ({ vehicleId, currentUser }) => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => ({
    date: new Date().toISOString().slice(0, 10),
    notes: '',
    checks: Object.fromEntries(Object.entries(INSPECTION_CATEGORIES).flatMap(([cat, conf]) => conf.items.map(it => [`${cat}.${it.key}`, true]))),
    photos: [],
  }));

  const fetchInspections = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/vehicles/${vehicleId}/inspections`, { withCredentials: true });
      setInspections(res.data || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { fetchInspections(); }, [vehicleId]);

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    setForm(f => ({ ...f, photos: [...f.photos, ...files] }));
  };
  const removePhoto = (idx) => setForm(f => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('date', form.date);
      fd.append('notes', form.notes);
      fd.append('checks', JSON.stringify(form.checks));
      form.photos.forEach((file) => fd.append('photos', file));
      await axios.post(`${API_URL}/api/vehicles/${vehicleId}/inspections`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Etat des lieux enregistre');
      setShowForm(false);
      setForm(prev => ({ ...prev, photos: [], notes: '' }));
      fetchInspections();
    } catch (err) {
      toast.error('Erreur enregistrement');
    } finally { setSubmitting(false); }
  };

  const deleteInspection = async (id) => {
    if (!window.confirm('Supprimer cet etat des lieux ?')) return;
    try {
      await axios.delete(`${API_URL}/api/vehicles/${vehicleId}/inspections/${id}`, { withCredentials: true });
      toast.success('Supprime');
      fetchInspections();
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="card" style={{ padding: '24px', textAlign: 'center', color: '#5E5F66' }}>Chargement...</div>;

  return (
    <div data-testid="vehicle-inspections-tab">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button className="btn btn-primary" onClick={() => setShowForm(s => !s)} data-testid="new-inspection-btn">
          <Plus size={16} /> {showForm ? 'Fermer' : 'Nouvel etat des lieux'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="card" style={{ padding: '20px', marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '14px', marginBottom: '14px' }}>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input type="text" className="form-input" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observations..." />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginBottom: '14px' }}>
            {Object.entries(INSPECTION_CATEGORIES).map(([cat, conf]) => (
              <div key={cat} style={{ padding: '12px', backgroundColor: '#F8F9FB', borderRadius: '8px' }}>
                <div style={{ fontWeight: 700, marginBottom: '8px', color: '#002FA7' }}>{conf.label}</div>
                {conf.items.map(it => {
                  const k = `${cat}.${it.key}`;
                  const checked = !!form.checks[k];
                  return (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', cursor: 'pointer', fontSize: '0.85rem' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setForm(f => ({ ...f, checks: { ...f.checks, [k]: e.target.checked } }))}
                        data-testid={`check-${k}`}
                      />
                      <span style={{ color: checked ? '#2E7D32' : '#C62828', fontWeight: checked ? 500 : 600 }}>{it.label}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="form-group">
            <label className="form-label">Reportage photo</label>
            <input type="file" accept="image/*" multiple onChange={handlePhotoChange} data-testid="inspection-photos-input" />
            {form.photos.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                {form.photos.map((p, i) => (
                  <div key={i} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #E2E2E6' }}>
                    <img src={URL.createObjectURL(p)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => removePhoto(i)} style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="submit-inspection">{submitting ? 'Enregistrement...' : 'Enregistrer'}</button>
          </div>
        </form>
      )}

      {inspections.length === 0 ? (
        <div className="card empty-state"><Camera size={48} className="empty-state-icon" /><p className="empty-state-text">Aucun etat des lieux enregistre</p></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {inspections.map(insp => {
            const totalItems = Object.values(insp.checks || {}).length;
            const okItems = Object.values(insp.checks || {}).filter(Boolean).length;
            const koItems = totalItems - okItems;
            return (
              <div key={insp.id} className="card" style={{ padding: '14px 18px' }} data-testid={`inspection-${insp.id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Etat des lieux du {insp.date}</div>
                    <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginTop: '4px' }}>
                      Par <strong>{insp.user_name || '-'}</strong> - {okItems}/{totalItems} OK
                      {koItems > 0 && <span style={{ color: '#C62828', marginLeft: '8px' }}>{koItems} point(s) a verifier</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {(insp.photos_count || 0) > 0 && <span style={{ fontSize: '0.78rem', color: '#1565C0', display: 'flex', alignItems: 'center', gap: '4px' }}><Camera size={14} /> {insp.photos_count}</span>}
                    {currentUser?.role === 'admin' && <button className="btn btn-danger btn-sm" onClick={() => deleteInspection(insp.id)}><Trash size={14} /></button>}
                  </div>
                </div>
                {insp.notes && <div style={{ fontSize: '0.85rem', color: '#5E5F66', marginTop: '8px', fontStyle: 'italic' }}>{insp.notes}</div>}
                {(insp.photos || []).length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
                    {insp.photos.map((p, i) => (
                      <a key={i} href={`${API_URL}${p.url}`} target="_blank" rel="noreferrer">
                        <img src={`${API_URL}${p.url}`} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #E2E2E6' }} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const VehicleDetail = ({ vehicle: v, activeTab, setActiveTab, vehicleExpenses, vehicleDocs, employees = [], currentUser, onBack, onEdit, onDelete, onSell, onShowExpenseModal, onShowDocModal, onDeleteExpense, onDeleteDoc, onAssignChange, ExpenseModal, DocModal, SellModal }) => (
  <div data-testid="vehicle-detail">
    <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: '8px' }}>Retour a la liste</button>
        <h1 className="page-title">{v.brand} {v.model}</h1>
        <p className="page-subtitle">{v.registration} - {VEHICLE_TYPES.find(t => t.value === v.vehicle_type)?.label}</p>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {!v.sold && <button className="btn btn-secondary" onClick={onSell}><Tag size={16} /> Vendre</button>}
        <button className="btn btn-secondary" onClick={() => onEdit(v)}><PencilSimple size={16} /> Modifier</button>
        <button className="btn btn-danger" onClick={() => onDelete(v.id)}><Trash size={16} /></button>
      </div>
    </div>

    {v.sold && (
      <div style={{ backgroundColor: '#FFF3E0', border: '1px solid #FFB74D', borderRadius: '4px', padding: '12px 16px', marginBottom: '16px' }}>
        Vehicule vendu le {v.sold_date} pour {v.sold_price?.toLocaleString('fr-FR')}EUR
      </div>
    )}

    <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E2E2E6', marginBottom: '24px', overflowX: 'auto' }}>
      {[
        { key: 'info', label: 'Informations' },
        { key: 'expenses', label: `Depenses (${vehicleExpenses.length})` },
        { key: 'documents', label: `Documents (${vehicleDocs.length})` },
        { key: 'trips', label: 'Historique des trajets' },
        { key: 'inspections', label: 'Etat des lieux' },
      ].map((tab) => (
        <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`vehicle-tab-${tab.key}`} style={{ padding: '12px 20px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontWeight: activeTab === tab.key ? '700' : '400', borderBottom: activeTab === tab.key ? '2px solid #002FA7' : '2px solid transparent', marginBottom: '-2px', color: activeTab === tab.key ? '#002FA7' : '#5E5F66', whiteSpace: 'nowrap' }}>
          {tab.label}
        </button>
      ))}
    </div>

    {activeTab === 'info' && (
      <div className="card">
        {v.photo_url && (
          <div style={{ width: '100%', maxHeight: '320px', overflow: 'hidden', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', background: '#F4F5F7' }}>
            <img
              src={`${API_URL}${v.photo_url}?v=${v.id}`}
              alt={`${v.brand} ${v.model}`}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              style={{ width: '100%', maxHeight: '320px', objectFit: 'cover', display: 'block' }}
            />
          </div>
        )}
        <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
          <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Marque / Modele</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{v.brand} {v.model}</div></div>
          <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Type</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{VEHICLE_TYPES.find(t => t.value === v.vehicle_type)?.label}</div></div>
          <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Immatriculation</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{v.registration || '-'}</div></div>
          <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Date d'achat</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{v.purchase_date || '-'}</div></div>
          <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Prix d'achat</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{v.purchase_price?.toLocaleString('fr-FR')}EUR</div></div>
          <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Etat</span><div style={{ marginTop: '4px' }}>{conditionBadge(v.condition)}</div></div>
          <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Assurance</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{v.insurance_start && v.insurance_end ? `Du ${v.insurance_start} au ${v.insurance_end}` : '-'}</div></div>
          <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Controle technique</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{v.technical_control_date || '-'}</div></div>
          <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Total depenses</span><div style={{ fontWeight: '700', marginTop: '4px', color: '#C62828' }}>{v.total_expenses.toLocaleString('fr-FR')}EUR</div></div>
        </div>
        {/* Affectation salarie + display */}
        <div style={{ borderTop: '1px solid #E2E2E6', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserCircle size={20} color="#1565C0" />
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Salarie affecte :</span>
            {v.assigned_user_name ? (
              <span style={{ fontWeight: 700, color: '#1565C0' }} data-testid="vehicle-assigned-name">{v.assigned_user_name}</span>
            ) : (
              <span style={{ color: '#5E5F66', fontStyle: 'italic' }}>Aucun</span>
            )}
          </div>
          <div style={{ maxWidth: '420px' }}>
            <InlineAssignSelect vehicle={v} employees={employees} onSaved={onAssignChange} />
          </div>
          <small style={{ color: '#5E5F66', fontSize: '0.75rem' }}>Le salarie affecte pourra consulter les documents et l'etat des lieux du vehicule depuis son application.</small>
        </div>
      </div>
    )}

    {activeTab === 'expenses' && (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}><button className="btn btn-primary" onClick={onShowExpenseModal} data-testid="add-vehicle-expense-btn"><Plus size={16} /> Ajouter une depense</button></div>
        <div className="card">
          {vehicleExpenses.length > 0 ? (
            <table className="data-table"><thead><tr><th>Date</th><th>Categorie</th><th>Description</th><th>HT</th><th>TVA</th><th>TTC</th><th></th></tr></thead><tbody>
              {vehicleExpenses.map((e) => (<tr key={e.id}><td>{e.date}</td><td>{e.category}</td><td>{e.description}</td><td>{e.amount_ht.toLocaleString('fr-FR')}EUR</td><td>{e.amount_tva.toLocaleString('fr-FR')}EUR</td><td style={{ fontWeight: '600' }}>{e.amount_ttc.toLocaleString('fr-FR')}EUR</td><td><button className="btn btn-danger btn-sm" onClick={() => onDeleteExpense(e.id)}><Trash size={14} /></button></td></tr>))}
            </tbody></table>
          ) : (<div className="empty-state"><Receipt size={48} className="empty-state-icon" /><p className="empty-state-text">Aucune depense</p></div>)}
        </div>
      </div>
    )}

    {activeTab === 'documents' && (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}><button className="btn btn-primary" onClick={onShowDocModal} data-testid="add-vehicle-doc-btn"><Plus size={16} /> Ajouter un document</button></div>
        <div className="card">
          {vehicleDocs.length > 0 ? (
            <table className="data-table"><thead><tr><th>Titre</th><th>Categorie</th><th>Fichier</th><th>Date</th><th></th></tr></thead><tbody>
              {vehicleDocs.map((d) => (<tr key={d.id}><td style={{ fontWeight: '600' }}>{d.title}</td><td>{d.category}</td><td>{d.original_filename}</td><td>{d.created_at?.slice(0, 10)}</td><td><div style={{ display: 'flex', gap: '4px' }}><a href={`${API_URL}/api/vehicles/${v.id}/documents/download/${d.id}`} className="btn btn-secondary btn-sm" data-testid={`download-doc-${d.id}`}><DownloadSimple size={14} /></a><button className="btn btn-danger btn-sm" onClick={() => onDeleteDoc(d.id)}><Trash size={14} /></button></div></td></tr>))}
            </tbody></table>
          ) : (<div className="empty-state"><FileText size={48} className="empty-state-icon" /><p className="empty-state-text">Aucun document</p></div>)}
        </div>
      </div>
    )}

    {activeTab === 'trips' && <TripsTab vehicleId={v.id} />}
    {activeTab === 'inspections' && <InspectionsTab vehicleId={v.id} currentUser={currentUser} />}

    {ExpenseModal}
    {DocModal}
    {SellModal}
  </div>
);
